import { ReactNode } from "react"
import type { MobileResultsSheet } from "./useMobileResultsSheet"

/**
 * Presentational chrome for the mobile results sheet (#856). The state lives
 * in useMobileResultsSheet; ExplorerContent places these pieces so the
 * results grid keeps a stable React position across the layout switch.
 */

const ICON_PROPS = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const

const SearchIcon = (
  <svg {...ICON_PROPS} width="18" height="18"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></svg>
)
const FilterIcon = (
  <svg {...ICON_PROPS} width="20" height="20"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
)
const MapIcon = (
  <svg {...ICON_PROPS} width="16" height="16"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" /><path d="M9 4v14M15 6v14" /></svg>
)
const ListIcon = (
  <svg {...ICON_PROPS} width="16" height="16"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
)

/**
 * Fixed top bar: Searchbar Pro's own mobile trigger IS the CTA (the module
 * relocates SBP's trigger into its topbar, js:424-459); the legacy bar gets
 * the module's "Search stays" pill, which opens the filter panel.
 */
export function SheetTopbar({ sheet, pro, filters }: { sheet: MobileResultsSheet; pro: boolean; filters: ReactNode }) {
  return (
    <div className="hfse-mres-topbar" ref={sheet.topbarRef}>
      {pro ? filters : (
        <button
          type="button"
          className="hfse-mres-cta"
          ref={sheet.ctaRef}
          aria-expanded={sheet.filtersOpen}
          aria-label="Search and filters"
          onClick={sheet.openFilters}
        >
          <span className="hfse-mres-cta-ico">{SearchIcon}</span>
          <span className="hfse-mres-cta-main">
            <span className="hfse-mres-cta-title">Search stays</span>
            <span className="hfse-mres-cta-sub">{sheet.countLabel}</span>
          </span>
          <span className="hfse-mres-cta-filt">{FilterIcon}</span>
        </button>
      )}
    </div>
  )
}

/** Legacy bar only: the full-screen filter panel (module `.hfse-body` proxy, js:577-603). */
export function SheetFilterPanel({ sheet, filters }: { sheet: MobileResultsSheet; filters: ReactNode }) {
  return (
    <div
      className="hfse-mres-filters"
      ref={sheet.panelRef}
      role="dialog"
      aria-modal={sheet.filtersOpen}
      aria-label="Search and filters"
      aria-hidden={!sheet.filtersOpen}
      onSubmitCapture={sheet.closeFilters}
      onClickCapture={sheet.onFiltersClickCapture}
    >
      <button type="button" className="hfse-mres-filter-close" aria-label="Close filters" onClick={sheet.closeFilters}>
        ✕
      </button>
      {filters}
    </div>
  )
}

/**
 * Grab handle + head, rendered INSIDE the sheet after the grid and pinned
 * to the top with CSS (`order: -1; position: sticky`) so the grid keeps its
 * DOM position while the head stays visible during list scrolling.
 */
export function SheetHandle({ sheet, header }: { sheet: MobileResultsSheet; header?: ReactNode }) {
  return (
    <div className="hfse-mres-handle">
      <button
        type="button"
        className="hfse-mres-grab"
        aria-label={sheet.listMode ? 'Collapse the results to show the map' : 'Expand the results list'}
        onClick={sheet.onGrabClick}
      >
        <i aria-hidden />
      </button>
      <div className="hfse-mres-head">
        <div className="hfse-mres-count" aria-live="polite">{sheet.countLabel}</div>
        <div className="hfse-mres-sub">{sheet.listMode ? 'Swipe down for the map' : 'Swipe up to browse all stays'}</div>
        {header}
      </div>
    </div>
  )
}

/** Floating Map/List pill (js:551-560; always visible so the list never traps). */
export function SheetFab({ sheet }: { sheet: MobileResultsSheet }) {
  return (
    <button
      type="button"
      className="hfse-mres-fab"
      aria-label={sheet.listMode ? 'Show map' : 'Show list'}
      onClick={sheet.toggleListMode}
    >
      <span className="hfse-mres-fab-ico">{sheet.listMode ? MapIcon : ListIcon}</span>
      {sheet.listMode ? 'Map' : 'List'}
    </button>
  )
}
